import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Instagram, Facebook, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill all fields");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Farina will reply soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-card/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <p className="text-xs uppercase tracking-[0.25em] text-secondary mb-4">Get in touch</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">Let's begin a conversation.</h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Have a question, a curiosity, or simply want to say hello? I'd love to hear from you.
            </p>

            <ul className="mt-10 space-y-5 text-sm">
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-secondary/30 flex items-center justify-center"><Phone size={16} /></span>
                <a href="tel:0211117540" data-testid="contact-phone" className="text-foreground hover:text-secondary">0211 117 540</a>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-secondary/30 flex items-center justify-center"><Mail size={16} /></span>
                <a href="mailto:ummesaad1989@gmail.com" data-testid="contact-email" className="text-foreground hover:text-secondary">ummesaad1989@gmail.com</a>
              </li>
              <li className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-secondary/30 flex items-center justify-center"><MapPin size={16} /></span>
                <span className="text-foreground">Serving Auckland & surrounding suburbs</span>
              </li>
            </ul>

            <div className="mt-10 flex gap-3">
              <a href="#" data-testid="ig-link" className="w-11 h-11 rounded-full border border-border/60 flex items-center justify-center hover:bg-secondary/20 transition-colors"><Instagram size={16} /></a>
              <a href="#" data-testid="fb-link" className="w-11 h-11 rounded-full border border-border/60 flex items-center justify-center hover:bg-secondary/20 transition-colors"><Facebook size={16} /></a>
            </div>

            <div className="mt-10 rounded-3xl overflow-hidden border border-border/60 bg-background h-48 flex items-center justify-center text-muted-foreground text-sm" data-testid="map-placeholder">
              Service-area map placeholder
            </div>
          </div>

          <form onSubmit={submit} data-testid="contact-form" className="lg:col-span-7 reveal rounded-[36px] bg-background border border-border/60 p-8 md:p-12 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Name</Label>
                <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-card/40 border-border/60 h-12" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Email</Label>
                <Input type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl bg-card/40 border-border/60 h-12" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Message</Label>
              <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={6} className="rounded-2xl bg-card/40 border-border/60" placeholder="Tell me what's on your heart..." />
            </div>
            <Button type="submit" disabled={sending} data-testid="contact-submit" className="rounded-full px-10 py-6 bg-foreground text-background hover:bg-foreground/85">
              {sending ? <><Loader2 className="mr-2 animate-spin" size={16}/> Sending…</> : "Send message"}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
